import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api/axiosConfig';
import Pagination from './Pagination';
import IssueRegisterModal from './IssueRegisterModal';
import { toLocalDateString } from '../utils/localDate';

function IssueList() {
    const navigate = useNavigate();
    const location = useLocation();
    const [issues, setIssues] = useState([]);
    const [pageInfo, setPageInfo] = useState({ page: 1, total: 0, limit: 20 });
    const [loading, setLoading] = useState(true);
    const [showRegisterModal, setShowRegisterModal] = useState(false);
    const [editingIssueId, setEditingIssueId] = useState(null);

    /**
     * 거르기는 서버가 한다.
     *
     * 예전에는 전부 받아 화면에서 걸렀다. 쪽을 나누면 한 쪽 안에서만 걸러져,
     * 조건에 맞는 것이 다음 쪽에 있으면 없는 것처럼 보인다.
     */
    const fetchIssues = useCallback(async (page = 1, append = false) => {
        setLoading(true);
        try {
            const { filter, resolved, facilityId } = location.state || {};
            const { data } = await api.get('/issues', {
                params: {
                    page,
                    limit: 20,
                    ...(facilityId && facilityId !== 'all' ? { facilityId } : {}),
                    ...(resolved === undefined ? {} : { resolved }),
                    ...(filter === 'today' ? { date: toLocalDateString() } : {}),
                },
            });
            setIssues((prev) => (append ? [...prev, ...(data.items || [])] : data.items || []));
            setPageInfo({ page: data.page, total: data.total, limit: data.limit });
        } catch (error) {
            console.error('Error fetching issues:', error);
        } finally {
            setLoading(false);
        }
    }, [location.state]);

    useEffect(() => {
        fetchIssues(1, false);
    }, [fetchIssues]);

    const handleIssueCreated = () => {
        fetchIssues();
        setShowRegisterModal(false);
        setEditingIssueId(null);
    };



    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        const days = ['일', '월', '화', '수', '목', '금', '토'];
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const dayName = days[date.getDay()];
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}.${month}.${day}(${dayName}) ${hours}:${minutes}`;
    };

    const getStatusBadge = (status) => {
        const badgeStyle = {
            padding: '0.35em 0.65em',
            fontSize: '0.75em',
            fontWeight: '600',
            borderRadius: '1rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
        };

        switch (status) {
            case 'REPORTED':
                return <span className="badge bg-danger bg-opacity-10 text-danger" style={badgeStyle}>신고됨</span>;
            case 'IN_PROGRESS':
                return <span className="badge bg-warning bg-opacity-10 text-warning" style={badgeStyle}>처리중</span>;
            case 'RESOLVED':
                return <span className="badge bg-primary bg-opacity-10 text-primary" style={badgeStyle}>해결 완료</span>;
            case 'WAITING':
                return <span className="badge bg-secondary bg-opacity-10 text-secondary" style={badgeStyle}>처리 대기 중</span>;
            default:
                return <span className="badge bg-light text-dark border" style={badgeStyle}>{status}</span>;
        }
    };

    if (loading && issues.length === 0) {
        return <div className="text-center p-5"><div className="spinner-border text-primary" role="status"></div></div>;
    }

    return (
        <div className="pb-5">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h4 className="fw-bold mb-1">이슈 관리</h4>
                    <p className="text-muted mb-0 small">등록된 이슈 현황을 조회하고 관리합니다.</p>
                </div>
                <button className="btn btn-primary px-3 fw-bold d-flex align-items-center justify-content-center" onClick={() => setShowRegisterModal(true)}>
                    <i className="bi bi-plus-lg"></i>
                    <span className="d-none d-md-inline ms-2">이슈 등록</span>
                </button>
            </div>

            {issues.length > 0 ? (
                <>
                    {/* Desktop View - Table */}
                    <div className="bg-white border rounded-3 overflow-hidden d-none d-md-block">
                        <table className="table table-hover align-middle mb-0">
                            <thead className="bg-light text-muted small">
                                <tr>
                                    <th className="ps-4 py-2 border-0" style={{ width: '40px' }}>
                                        <input type="checkbox" className="form-check-input" />
                                    </th>
                                    <th className="py-2 border-0">제목</th>
                                    <th className="py-2 border-0">시설</th>
                                    <th className="py-2 border-0">구역</th>
                                    <th className="py-2 border-0">이슈 상태</th>
                                    <th className="py-2 border-0">등록일</th>
                                    <th className="py-2 border-0">등록한 사람</th>
                                    <th className="pe-4 py-2 border-0">해결일</th>
                                </tr>
                            </thead>
                            <tbody>
                                {issues.map(issue => (
                                    <tr key={issue.id} onClick={() => navigate(`/issues/${issue.id}`)} style={{ cursor: 'pointer' }}>
                                        <td className="ps-4" onClick={(e) => e.stopPropagation()}>
                                            <input type="checkbox" className="form-check-input" />
                                        </td>
                                        <td className="fw-medium">{issue.title}</td>
                                        <td className="text-secondary small">{issue.facility?.name}</td>
                                        <td className="text-secondary small">
                                            {issue.section?.name ? (
                                                <span className="badge bg-light text-dark border">{issue.section.name}</span>
                                            ) : '-'}
                                        </td>
                                        <td>{getStatusBadge(issue.status)}</td>
                                        <td className="text-secondary small">{formatDate(issue.createdAt)}</td>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <div className="bg-light rounded-circle text-center me-2" style={{ width: '24px', height: '24px', lineHeight: '24px', fontSize: '10px' }}>
                                                    {issue.reporter?.name?.charAt(0) || 'U'}
                                                </div>
                                                <small>{issue.reporter?.name}</small>
                                            </div>
                                        </td>
                                        <td className="pe-4 text-muted small">{issue.resolvedAt ? formatDate(issue.resolvedAt) : '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile View - Cards */}
                    <div className="d-md-none">
                        {issues.map(issue => (
                            <div key={issue.id} className="card border-0 shadow-sm mb-3" onClick={() => navigate(`/issues/${issue.id}`)}>
                                <div className="card-body">
                                    <div className="mb-2">
                                        <div className="d-flex justify-content-between align-items-start mb-1">
                                            <h5 className="card-title fw-bold mb-0 text-truncate" style={{ maxWidth: '70%' }}>{issue.title}</h5>
                                            <div style={{ flexShrink: 0 }}>{getStatusBadge(issue.status)}</div>
                                        </div>
                                        <p className="card-text text-secondary mb-2 small text-truncate">
                                            <i className="bi bi-geo-alt me-1"></i>
                                            {issue.facility?.name} {issue.section?.name && `> ${issue.section.name}`}
                                        </p>
                                    </div>

                                    <div className="d-flex justify-content-between align-items-end mt-3 border-top pt-2">
                                        <div className="small text-muted">
                                            <div>등록: {formatDate(issue.createdAt)}</div>
                                            {issue.resolvedAt && <div>해결: {formatDate(issue.resolvedAt)}</div>}
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <div className="bg-light rounded-circle text-center me-2" style={{ width: '24px', height: '24px', lineHeight: '24px', fontSize: '10px' }}>
                                                {issue.reporter?.name?.charAt(0) || 'U'}
                                            </div>
                                            <small className="text-secondary">{issue.reporter?.name}</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            ) : (
                <div className="text-center p-5 card border-0 shadow-sm">
                    <div className="mb-3 text-muted">
                        <i className="bi bi-clipboard-x" style={{ fontSize: '3rem' }}></i>
                    </div>
                    <h5 className="text-muted">등록된 이슈가 없습니다.</h5>
                    <p className="text-muted small">새로운 이슈를 등록해보세요.</p>
                </div>
            )}

            <Pagination
                page={pageInfo.page}
                total={pageInfo.total}
                limit={pageInfo.limit}
                loading={loading}
                onChange={fetchIssues}
            />

            {/* Modals */}
            {showRegisterModal && (
                <IssueRegisterModal
                    show={showRegisterModal}
                    issueId={editingIssueId}
                    onClose={() => {
                        setShowRegisterModal(false);
                        setEditingIssueId(null);
                    }}
                    onSuccess={handleIssueCreated}
                />
            )}
        </div>
    );
}

export default IssueList;
